import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import useAuthStore from '../store/authStore';

/**
 * Profile completion card for sidebar
 * Shows user avatar, profile completion progress, and incomplete sections
 */
const ProfileCompletionCard = ({ upworkSummary = null, className = '' }) => {
  const { user } = useAuthStore();
  const upworkProfile = upworkSummary?.profile || null;
  const profileDataSource = upworkSummary?.profileDataSource || null;
  const usingImportedProfile = profileDataSource === 'upwork_page_import' || profileDataSource === 'mixed';
  const upworkConnected = upworkSummary?.connected === true || !!profileDataSource;
  const canManageIntegrations = ['admin', 'superuser', 'owner'].includes(user?.role);
  const integrationsPath = canManageIntegrations ? '/admin/integrations' : '/profile';

  // Calculate profile completion based on filled fields
  const calculateCompletion = () => {
    const skillsCount = Number(upworkProfile?.skillsCount || 0);
    const portfolioCount = Number(upworkProfile?.portfolioCount || 0);

    const fields = [
      { name: 'Name', complete: !!user?.name },
      { name: 'Email', complete: !!user?.email },
      { name: 'Upwork Connected', complete: upworkConnected },
      { name: 'Profile Bio', complete: !!upworkProfile?.bio },
      { name: 'Skills', complete: skillsCount > 0 },
      { name: 'Portfolio', complete: portfolioCount > 0 },
    ];

    const completedCount = fields.filter(f => f.complete).length;
    const calculated = Math.round((completedCount / fields.length) * 100);
    const percentage = Number.isFinite(Number(upworkProfile?.completionPercent))
      ? Math.max(Math.min(Number(upworkProfile.completionPercent), 100), 0)
      : calculated;
    const incomplete = fields.filter(f => !f.complete);

    return { percentage, incomplete };
  };

  const { percentage, incomplete } = calculateCompletion();

  // Get user initials
  const getInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
      {/* User Avatar and Info */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-14 h-14 rounded-full bg-primary-600 flex items-center justify-center">
          <span className="text-white text-xl font-medium">{getInitials()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <Link
            to={integrationsPath}
            className="text-sm font-semibold text-gray-900 hover:text-primary-600 truncate block"
          >
            {user?.name || 'Your Name'}
          </Link>
          <p className="text-xs text-gray-500 truncate">
            {upworkProfile?.title || user?.role || 'Add your title'}
          </p>
          {usingImportedProfile && upworkSummary?.connected !== true && (
            <p className="text-[11px] text-amber-600 truncate">Imported profile snapshot</p>
          )}
        </div>
      </div>

      {/* Profile Completion Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">Profile Completion</span>
          <span className="text-xs font-semibold text-primary-600">{percentage}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Incomplete Sections */}
      {incomplete.length > 0 && percentage < 100 && (
        <div className="space-y-2 mb-4">
          <p className="text-xs text-gray-500">Complete your profile:</p>
          {incomplete.slice(0, 3).map((item) => (
            <div key={item.name} className="flex items-center gap-2 text-xs text-gray-600">
              <AlertCircle className="w-3 h-3 text-amber-500" />
              <span>
                {item.name === 'Upwork Connected' ? 'Connect Upwork account' : `Add ${item.name}`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Complete Profile Link */}
      <Link
        to={integrationsPath}
        className="block w-full text-center py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
      >
        {upworkSummary?.connected === true
          ? 'Manage Upwork profile'
          : usingImportedProfile
            ? 'Manage imported profile'
            : canManageIntegrations
              ? 'Connect Upwork'
              : 'View profile'}
      </Link>
    </div>
  );
};

export default ProfileCompletionCard;
